import { api } from "services/restUtil";
import { pollWithBackoff } from "utils/async";
import { defineApiQuery } from "utils/queries";
import { WithOptional } from "utils/types";

type CreateAiExercisePayload = {
  count: 1 | 2 | 3 | 4 | 5;
  context?: string | null;
  tally_conversation_id?: string | null;
};
type CreateExerciseData = WithOptional<CreateAiExercisePayload, "count"> | undefined;
type CreateAiExerciseResponse = {
  id: string;
};

const DEFAULT_EXERCISE_COUNT = 2;

export function createAiExercises(payload: CreateExerciseData) {
  return api.post<CreateAiExercisePayload, CreateAiExerciseResponse>("/ai/exercises", {
    count: DEFAULT_EXERCISE_COUNT,
    ...payload,
  });
}

export type AiExerciseSequence = {
  id: string;
  sentences: AiExercise[]; // Should be called "exercises"
};

export type AiExercise = {
  id: string;
  sentence: string;
  context: string;
  answers: AiExerciseAnswer[];
  is_correct?: boolean | null;
  submitted_at?: string | null;
};

export type AiExerciseAnswer = {
  id: string;
  word: string;
  explanation: string;
  is_correct: boolean;
  is_submitted: boolean;
};

const fetchAiExerciseSequence = async (id: string) => {
  try {
    const response = await api.get<Response>(`/ai/exercise/${id}`, undefined, {
      rawResponse: true,
    });

    if (response.status === 204) {
      throw new ExerciseNotReady("Exercise generation in progress");
    }
    return (await response.json()) as AiExerciseSequence;
  } catch (e: any) {
    if (e?.response?.status === 429) {
      // TODO how do we handle this?
      throw new TokenLimitReached("Limit reached");
    }
    throw e;
  }
};

export async function pollAiExerciseSequence(id: string) {
  return pollWithBackoff({
    fn: () => fetchAiExerciseSequence(id),
    timeout: 3 * 60 * 1000,
    minDelay: 2 * 1000,
    backoffFactor: 1.2,
    shouldRetry: ex => ex instanceof ExerciseNotReady,
  });
}

class TokenLimitReached extends Error {}
class ExerciseNotReady extends Error {}

export const aiExercisesQuery = defineApiQuery<AiExerciseSequence, { id: string }>({
  key: "/ai/exercise/:id",
  queryFn: async ({ params }) => fetchAiExerciseSequence(params.id),
});

type AiExerciseSubmittedAnswers = {
  answers: Array<{
    sentence_id: string;
    answer_id: string;
  }>;
};

export type SubmitAiExerciseAnswersVariables = {
  sequenceId: string;
  exerciseId: string;
  answerId: string;
};

export function submitAiExerciseAnswers(variables: SubmitAiExerciseAnswersVariables) {
  return api.post<AiExerciseSubmittedAnswers, AiExerciseSequence>(
    `/ai/exercise/${variables.sequenceId}/answer`,
    {
      answers: [{ sentence_id: variables.exerciseId, answer_id: variables.answerId }],
    }
  );
}

export const getSubmittedAnswer = (exercise: AiExercise) =>
  exercise.answers.find(ans => ans.is_submitted);
